import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { useParams } from 'react-router-dom';
import { LIST_SUBSCRIPTION_ITEMS_RESOURCE } from 'src/features/subscriptionItems/api/useListSubscriptionItems';
import { SubscriptionItem } from 'src/features/subscriptionItems/utils/types';
import { useUpdateSubscriptionItem } from 'src/features/subscriptionItems/api/useUpdateSubscriptionItem';
import SubscriptionItemFormModal from 'src/features/subscriptionItems/components/forms/SubscriptionItemFormModal';
import { subscriptionItemsMessages } from 'src/features/subscriptionItems/utils/messages';
import { SUBSCRIPTION_ITEM_RESOURCE } from 'src/features/subscriptionItems/api/useGetSubscriptionItem';

interface UpdateSubscriptionItemFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSubscriptionItem: SubscriptionItem;
    children?: React.ReactNode;
}

function UpdateSubscriptionItemFormModal<TData>({
    className,
    semantics,
    children,
    selectedSubscriptionItem,
}: UpdateSubscriptionItemFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { subscriptionId = '' } = useParams();

    const { mutate, isPending } = useUpdateSubscriptionItem({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEMS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_ITEM_RESOURCE] });
            toast({
                title: formatMessage(subscriptionItemsMessages.updateSubscriptionItemSuccess),
                description: formatMessage(subscriptionItemsMessages.updateSubscriptionItemSuccessDescription),
            });
        },
    });

    return (
        <SubscriptionItemFormModal
            selectedSubscriptionItem={selectedSubscriptionItem}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, itemId: selectedSubscriptionItem.id, subscriptionId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(subscriptionItemsMessages.editSubscriptionItemSubtitle)}
        >
            {children}
        </SubscriptionItemFormModal>
    );
}

export default UpdateSubscriptionItemFormModal;
